import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Api from "../../../../shared/api/api";

import "../../styles/DevelopmentAdmin.css";
import "../styles/DevelopmentInvoices.css";

const EMPTY_INVOICE_FORM = {
  client_id: "",
  project_id: "",
  invoice_number: "",
  invoice_type: "final",
  issue_date: new Date()
    .toISOString()
    .slice(0, 10),
  due_date: "",
  discount_amount: "0",
  tax_amount: "0",
  sent: false,
  status: "draft",
  payment_method: "",
  notes: "",
};

const EMPTY_ITEM_FORM = {
  catalog_item_id: "",
  title: "",
  description: "",
  quantity: "1",
  unit_price: "0",
  taxable: false,
  display_order: "0",
};

const EMPTY_PAYMENT_FORM = {
  payment_type: "final",
  amount: "",
  payment_method: "check",
  reference_number: "",
  notes: "",
  confirmed: true,
};

const INVOICE_TYPES = [
  "deposit",
  "progress",
  "final",
  "custom",
];

const STATUS_OPTIONS = [
  "draft",
  "sent",
  "partial",
  "paid",
  "overdue",
  "cancelled",
];

const PAYMENT_METHODS = [
  "check",
  "cash",
  "card",
  "stripe",
  "paypal",
  "zelle",
  "bank_transfer",
  "other",
];

function safeString(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value);
}

function cleanText(value) {
  return safeString(value).trim();
}

function optionalText(value) {
  const cleaned = cleanText(value);

  return cleaned || null;
}

function normalizeDate(value) {
  return value
    ? safeString(value).slice(0, 10)
    : "";
}

function formatCurrency(value) {
  const amount = Number(value);

  return new Intl.NumberFormat(
    "en-US",
    {
      style: "currency",
      currency: "USD",
    }
  ).format(
    Number.isFinite(amount)
      ? amount
      : 0
  );
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(
    `${normalizeDate(value)}T00:00:00`
  );

  if (
    Number.isNaN(date.getTime())
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  ).format(date);
}

function formatLabel(value) {
  return safeString(value)
    .replaceAll("_", " ")
    .replace(
      /\b\w/g,
      (letter) =>
        letter.toUpperCase()
    );
}

function getClientName(client) {
  if (!client) {
    return "Unknown client";
  }

  const personalName = [
    client.first_name,
    client.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    client.business_name ||
    personalName ||
    `Client ${client.id}`
  );
}

function normalizeInvoice(
  invoice = {}
) {
  return {
    client_id:
      invoice.client_id == null
        ? ""
        : String(
            invoice.client_id
          ),

    project_id:
      invoice.project_id == null
        ? ""
        : String(
            invoice.project_id
          ),

    invoice_number:
      safeString(
        invoice.invoice_number
      ),

    invoice_type:
      safeString(
        invoice.invoice_type
      ) || "final",

    issue_date:
      normalizeDate(
        invoice.issue_date
      ),

    due_date:
      normalizeDate(
        invoice.due_date
      ),

    discount_amount:
      invoice.discount_amount == null
        ? "0"
        : String(
            invoice.discount_amount
          ),

    tax_amount:
      invoice.tax_amount == null
        ? "0"
        : String(
            invoice.tax_amount
          ),

    sent:
      Number(invoice.sent) === 1,

    status:
      safeString(
        invoice.status
      ) || "draft",

    payment_method:
      safeString(
        invoice.payment_method
      ),

    notes:
      safeString(
        invoice.notes
      ),
  };
}

function normalizeItem(item = {}) {
  return {
    catalog_item_id:
      item.catalog_item_id == null
        ? ""
        : String(
            item.catalog_item_id
          ),

    title:
      safeString(item.title),

    description:
      safeString(
        item.description
      ),

    quantity:
      item.quantity == null
        ? "1"
        : String(item.quantity),

    unit_price:
      item.unit_price == null
        ? "0"
        : String(
            item.unit_price
          ),

    taxable:
      Number(item.taxable) === 1,

    display_order:
      item.display_order == null
        ? "0"
        : String(
            item.display_order
          ),
  };
}

function DevelopmentInvoices() {
  const [invoices, setInvoices] =
    useState([]);

  const [clients, setClients] =
    useState([]);

  const [projects, setProjects] =
    useState([]);

  const [
    catalogItems,
    setCatalogItems,
  ] = useState([]);

  const [
    selectedInvoice,
    setSelectedInvoice,
  ] = useState(null);

  const [
    invoiceForm,
    setInvoiceForm,
  ] = useState({
    ...EMPTY_INVOICE_FORM,
  });

  const [
    invoiceItems,
    setInvoiceItems,
  ] = useState([]);

  const [
    selectedItem,
    setSelectedItem,
  ] = useState(null);

  const [itemForm, setItemForm] =
    useState({
      ...EMPTY_ITEM_FORM,
    });

  const [
    paymentForm,
    setPaymentForm,
  ] = useState({
    ...EMPTY_PAYMENT_FORM,
  });

  const [
    showInvoiceForm,
    setShowInvoiceForm,
  ] = useState(false);

  const [
    showItemManager,
    setShowItemManager,
  ] = useState(false);

  const [
    showItemForm,
    setShowItemForm,
  ] = useState(false);

  const [
    showPaymentForm,
    setShowPaymentForm,
  ] = useState(false);

  const [search, setSearch] =
    useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all");

  const [
    clientFilter,
    setClientFilter,
  ] = useState("");

  const [isLoading, setIsLoading] =
    useState(true);

  const [isSaving, setIsSaving] =
    useState(false);

  const [
    isLoadingItems,
    setIsLoadingItems,
  ] = useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const loadData = useCallback(
    async () => {
      setIsLoading(true);
      setError("");

      try {
        const [
          invoiceResponse,
          clientResponse,
          projectResponse,
          catalogResponse,
        ] = await Promise.all([
          Api.getDevelopmentInvoices(),
          Api.getDevelopmentClients(),
          Api.getDevelopmentProjects(),
          Api.getDevelopmentInvoiceItemCatalog(
            true
          ),
        ]);

        setInvoices(
          invoiceResponse?.invoices ||
            []
        );

        setClients(
          clientResponse?.clients ||
            []
        );

        setProjects(
          projectResponse?.projects ||
            []
        );

        setCatalogItems(
          catalogResponse?.items ||
            []
        );
      } catch (requestError) {
        setError(
          requestError.message ||
            "Invoices could not be loaded."
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clientMap = useMemo(
    () =>
      new Map(
        clients.map((client) => [
          Number(client.id),
          client,
        ])
      ),
    [clients]
  );

  const projectMap = useMemo(
    () =>
      new Map(
        projects.map((project) => [
          Number(project.id),
          project,
        ])
      ),
    [projects]
  );

  const availableProjects =
    useMemo(() => {
      if (!invoiceForm.client_id) {
        return projects;
      }

      return projects.filter(
        (project) =>
          String(
            project.client_id
          ) ===
          String(
            invoiceForm.client_id
          )
      );
    }, [
      projects,
      invoiceForm.client_id,
    ]);

  const filteredInvoices =
    useMemo(() => {
      const normalizedSearch =
        search
          .trim()
          .toLowerCase();

      return invoices.filter(
        (invoice) => {
          if (
            statusFilter !== "all" &&
            String(
              invoice.status
            ) !== statusFilter
          ) {
            return false;
          }

          if (
            clientFilter &&
            String(
              invoice.client_id
            ) !==
              String(clientFilter)
          ) {
            return false;
          }

          if (!normalizedSearch) {
            return true;
          }

          const client =
            clientMap.get(
              Number(
                invoice.client_id
              )
            );

          const project =
            projectMap.get(
              Number(
                invoice.project_id
              )
            );

          const searchableText = [
            invoice.invoice_number,
            invoice.invoice_type,
            invoice.status,
            invoice.payment_method,
            getClientName(client),
            project?.title,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return searchableText.includes(
            normalizedSearch
          );
        }
      );
    }, [
      invoices,
      search,
      statusFilter,
      clientFilter,
      clientMap,
      projectMap,
    ]);

  const summary = useMemo(
    () => ({
      total:
        invoices.length,

      draft:
        invoices.filter(
          (invoice) =>
            invoice.status ===
            "draft"
        ).length,

      outstanding:
        invoices.filter(
          (invoice) =>
            Number(
              invoice.paid
            ) !== 1 &&
            invoice.status !==
              "cancelled"
        ).length,

      paid:
        invoices.filter(
          (invoice) =>
            Number(
              invoice.paid
            ) === 1
        ).length,

      balanceDue:
        invoices.reduce(
          (total, invoice) =>
            total +
            (
              Number(
                invoice.balance_due
              ) || 0
            ),
          0
        ),
    }),
    [invoices]
  );

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const openNewInvoiceForm = () => {
    clearMessages();

    setSelectedInvoice(null);

    setInvoiceForm({
      ...EMPTY_INVOICE_FORM,
    });

    setShowInvoiceForm(true);
  };

  const openEditInvoiceForm = (
    invoice
  ) => {
    clearMessages();

    setSelectedInvoice(invoice);

    setInvoiceForm(
      normalizeInvoice(invoice)
    );

    setShowInvoiceForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeInvoiceForm = () => {
    setSelectedInvoice(null);

    setInvoiceForm({
      ...EMPTY_INVOICE_FORM,
    });

    setShowInvoiceForm(false);
  };

  const handleInvoiceChange = (
    event
  ) => {
    const {
      name,
      value,
      checked,
      type,
    } = event.target;

    setInvoiceForm(
      (current) => {
        const next = {
          ...current,

          [name]:
            type === "checkbox"
              ? checked
              : value,
        };

        if (
          name === "client_id"
        ) {
          next.project_id = "";
        }

        if (
          name === "sent" &&
          checked &&
          next.status === "draft"
        ) {
          next.status = "sent";
        }

        if (
          name === "sent" &&
          !checked &&
          next.status === "sent"
        ) {
          next.status = "draft";
        }

        if (name === "status") {
          if (value === "draft") {
            next.sent = false;
          }

          if (
            [
              "sent",
              "partial",
              "paid",
              "overdue",
            ].includes(value)
          ) {
            next.sent = true;
          }
        }

        return next;
      }
    );
  };

  const validateInvoice = () => {
    if (!invoiceForm.client_id) {
      return "A client is required.";
    }

    if (!invoiceForm.project_id) {
      return "A project is required.";
    }

    if (!invoiceForm.issue_date) {
      return "Issue date is required.";
    }

    const discount =
      Number(
        invoiceForm.discount_amount ||
          0
      );

    const tax =
      Number(
        invoiceForm.tax_amount ||
          0
      );

    if (
      !Number.isFinite(discount) ||
      discount < 0
    ) {
      return "Discount must be 0 or greater.";
    }

    if (
      !Number.isFinite(tax) ||
      tax < 0
    ) {
      return "Tax must be 0 or greater.";
    }

    return "";
  };

  const buildInvoicePayload =
    () => ({
      client_id:
        Number(
          invoiceForm.client_id
        ),

      project_id:
        Number(
          invoiceForm.project_id
        ),

      invoice_number:
        cleanText(
          invoiceForm.invoice_number
        ) || undefined,

      invoice_type:
        cleanText(
          invoiceForm.invoice_type
        ) || "final",

      issue_date:
        normalizeDate(
          invoiceForm.issue_date
        ),

      due_date:
        normalizeDate(
          invoiceForm.due_date
        ) || null,

      discount_amount:
        Number(
          invoiceForm.discount_amount ||
            0
        ),

      tax_amount:
        Number(
          invoiceForm.tax_amount ||
            0
        ),

      sent:
        Boolean(
          invoiceForm.sent
        ),

      status:
        cleanText(
          invoiceForm.status
        ) || "draft",

      payment_method:
        optionalText(
          invoiceForm.payment_method
        ),

      notes:
        optionalText(
          invoiceForm.notes
        ),
    });

  const handleInvoiceSubmit =
    async (event) => {
      event.preventDefault();
      clearMessages();

      const validationError =
        validateInvoice();

      if (validationError) {
        setError(validationError);
        return;
      }

      setIsSaving(true);

      try {
        const payload =
          buildInvoicePayload();

        if (selectedInvoice) {
          await Api.updateDevelopmentInvoice(
            selectedInvoice.id,
            payload
          );

          setSuccess(
            "Invoice updated successfully."
          );
        } else {
          await Api.createDevelopmentInvoice(
            payload
          );

          setSuccess(
            "Invoice created successfully."
          );
        }

        await loadData();

        closeInvoiceForm();
      } catch (requestError) {
        setError(
          requestError.message ||
            "The invoice could not be saved."
        );
      } finally {
        setIsSaving(false);
      }
    };

  const markSent = async (
    invoice
  ) => {
    clearMessages();

    try {
      await Api.updateDevelopmentInvoice(
        invoice.id,
        {
          sent: true,
          status: "sent",
        }
      );

      setSuccess(
        "Invoice marked as sent."
      );

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The invoice could not be updated."
      );
    }
  };

  const deleteInvoice = async (
    invoice
  ) => {
    const hasPayments =
      Number(
        invoice.amount_paid || 0
      ) > 0;

    const confirmed =
      window.confirm(
        hasPayments
          ? `Invoice ${invoice.invoice_number} has payment history. It will be cancelled and preserved. Continue?`
          : `Delete invoice ${invoice.invoice_number}? This will also delete its invoice items.`
      );

    if (!confirmed) {
      return;
    }

    clearMessages();

    try {
      const response =
        await Api.deleteDevelopmentInvoice(
          invoice.id
        );

      setSuccess(
        response?.message ||
          (
            hasPayments
              ? "Invoice cancelled."
              : "Invoice deleted."
          )
      );

      if (
        Number(
          selectedInvoice?.id
        ) ===
        Number(invoice.id)
      ) {
        closeInvoiceForm();
        closeItemManager();
      }

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The invoice could not be deleted."
      );
    }
  };

  const openItemManager = async (
    invoice
  ) => {
    clearMessages();

    setSelectedInvoice(invoice);
    setInvoiceItems([]);
    setShowItemManager(true);
    setShowItemForm(false);
    setShowPaymentForm(false);
    setSelectedItem(null);

    setItemForm({
      ...EMPTY_ITEM_FORM,
    });

    setIsLoadingItems(true);

    try {
      const response =
        await Api.getDevelopmentInvoiceItems(
          invoice.id
        );

      setInvoiceItems(
        response?.items || []
      );
    } catch (requestError) {
      setError(
        requestError.message ||
          "Invoice items could not be loaded."
      );
    } finally {
      setIsLoadingItems(false);
    }
  };

  const closeItemManager = () => {
    setSelectedInvoice(null);
    setInvoiceItems([]);
    setShowItemManager(false);
    setShowItemForm(false);
    setShowPaymentForm(false);
    setSelectedItem(null);

    setItemForm({
      ...EMPTY_ITEM_FORM,
    });
  };

  const openNewItemForm = () => {
    setSelectedItem(null);

    setItemForm({
      ...EMPTY_ITEM_FORM,
    });

    setShowItemForm(true);
    setShowPaymentForm(false);
  };

  const openEditItemForm = (
    item
  ) => {
    setSelectedItem(item);

    setItemForm(
      normalizeItem(item)
    );

    setShowItemForm(true);
    setShowPaymentForm(false);
  };

  const handleItemChange = (
    event
  ) => {
    const {
      name,
      value,
      checked,
      type,
    } = event.target;

    if (
      name === "catalog_item_id"
    ) {
      if (!value) {
        setItemForm(
          (current) => ({
            ...current,
            catalog_item_id: "",
          })
        );

        return;
      }

      const selectedCatalogItem =
        catalogItems.find(
          (catalogItem) =>
            String(
              catalogItem.id
            ) === String(value)
        );

      if (!selectedCatalogItem) {
        setItemForm(
          (current) => ({
            ...current,
            catalog_item_id:
              value,
          })
        );

        return;
      }

      setItemForm(
        (current) => ({
          ...current,

          catalog_item_id:
            String(
              selectedCatalogItem.id
            ),

          title:
            safeString(
              selectedCatalogItem.item_name
            ),

          description:
            safeString(
              selectedCatalogItem.description
            ),

          unit_price:
            selectedCatalogItem.default_price ==
            null
              ? "0"
              : String(
                  selectedCatalogItem.default_price
                ),

          taxable:
            Number(
              selectedCatalogItem.taxable
            ) === 1,
        })
      );

      return;
    }

    setItemForm(
      (current) => ({
        ...current,

        [name]:
          type === "checkbox"
            ? checked
            : value,
      })
    );
  };

  const validateItem = () => {
    if (
      !cleanText(
        itemForm.title
      )
    ) {
      return "Invoice item title is required.";
    }

    const quantity =
      Number(
        itemForm.quantity
      );

    const unitPrice =
      Number(
        itemForm.unit_price
      );

    const displayOrder =
      Number(
        itemForm.display_order ||
          0
      );

    if (
      !Number.isFinite(quantity) ||
      quantity <= 0
    ) {
      return "Quantity must be greater than 0.";
    }

    if (
      !Number.isFinite(unitPrice) ||
      unitPrice < 0
    ) {
      return "Unit price must be 0 or greater.";
    }

    if (
      !Number.isInteger(
        displayOrder
      ) ||
      displayOrder < 0
    ) {
      return "Display order must be a whole number that is 0 or greater.";
    }

    return "";
  };

  const reloadInvoiceItems =
    async () => {
      if (!selectedInvoice) {
        return;
      }

      const response =
        await Api.getDevelopmentInvoiceItems(
          selectedInvoice.id
        );

      setInvoiceItems(
        response?.items || []
      );

      await loadData();

      const refreshed =
        await Api.getDevelopmentInvoice(
          selectedInvoice.id
        );

      if (refreshed?.invoice) {
        setSelectedInvoice(
          refreshed.invoice
        );
      }
    };

  const handleItemSubmit =
    async (event) => {
      event.preventDefault();
      clearMessages();

      const validationError =
        validateItem();

      if (validationError) {
        setError(validationError);
        return;
      }

      setIsSaving(true);

      const payload = {
        catalog_item_id:
          itemForm.catalog_item_id
            ? Number(
                itemForm.catalog_item_id
              )
            : null,

        title:
          cleanText(
            itemForm.title
          ),

        description:
          optionalText(
            itemForm.description
          ),

        quantity:
          Number(
            itemForm.quantity
          ),

        unit_price:
          Number(
            itemForm.unit_price
          ),

        taxable:
          Boolean(
            itemForm.taxable
          ),

        display_order:
          Number(
            itemForm.display_order ||
              0
          ),
      };

      try {
        if (selectedItem) {
          await Api.updateDevelopmentInvoiceItem(
            selectedItem.id,
            payload
          );

          setSuccess(
            "Invoice item updated."
          );
        } else {
          await Api.createDevelopmentInvoiceItem(
            selectedInvoice.id,
            payload
          );

          setSuccess(
            "Invoice item created."
          );
        }

        await reloadInvoiceItems();

        setSelectedItem(null);

        setItemForm({
          ...EMPTY_ITEM_FORM,
        });

        setShowItemForm(false);
      } catch (requestError) {
        setError(
          requestError.message ||
            "The invoice item could not be saved."
        );
      } finally {
        setIsSaving(false);
      }
    };

  const deleteItem = async (
    item
  ) => {
    const confirmed =
      window.confirm(
        `Delete invoice item "${item.title}"?`
      );

    if (!confirmed) {
      return;
    }

    clearMessages();

    try {
      await Api.deleteDevelopmentInvoiceItem(
        item.id
      );

      setSuccess(
        "Invoice item deleted."
      );

      await reloadInvoiceItems();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The invoice item could not be deleted."
      );
    }
  };

  const openPaymentForm = () => {
    setPaymentForm({
      ...EMPTY_PAYMENT_FORM,

      payment_type:
        selectedInvoice
          ?.invoice_type ||
        "final",

      amount:
        selectedInvoice
          ?.balance_due == null
          ? ""
          : String(
              selectedInvoice.balance_due
            ),
    });

    setShowPaymentForm(true);
    setShowItemForm(false);
  };

  const handlePaymentChange = (
    event
  ) => {
    const {
      name,
      value,
      checked,
      type,
    } = event.target;

    setPaymentForm(
      (current) => ({
        ...current,

        [name]:
          type === "checkbox"
            ? checked
            : value,
      })
    );
  };

  const handlePaymentSubmit =
    async (event) => {
      event.preventDefault();
      clearMessages();

      const amount =
        Number(
          paymentForm.amount
        );

      if (
        !Number.isFinite(amount) ||
        amount <= 0
      ) {
        setError(
          "Payment amount must be greater than 0."
        );

        return;
      }

      setIsSaving(true);

      try {
        await Api.createDevelopmentPayment({
          client_id:
            Number(
              selectedInvoice.client_id
            ),

          project_id:
            Number(
              selectedInvoice.project_id
            ),

          invoice_id:
            Number(
              selectedInvoice.id
            ),

          payment_type:
            cleanText(
              paymentForm.payment_type
            ) || "final",

          amount,

          payment_method:
            optionalText(
              paymentForm.payment_method
            ),

          reference_number:
            optionalText(
              paymentForm.reference_number
            ),

          notes:
            optionalText(
              paymentForm.notes
            ),

          confirmed:
            Boolean(
              paymentForm.confirmed
            ),
        });

        setSuccess(
          "Payment recorded successfully."
        );

        setShowPaymentForm(false);

        setPaymentForm({
          ...EMPTY_PAYMENT_FORM,
        });

        await reloadInvoiceItems();
      } catch (requestError) {
        setError(
          requestError.message ||
            "The payment could not be recorded."
        );
      } finally {
        setIsSaving(false);
      }
    };

  return (
    <div className="development-invoices-page">
      <div className="development-admin-page-heading">
        <div>
          <p className="development-admin-eyebrow">
            Billing management
          </p>

          <h2>Invoices</h2>

          <p>
            Create itemized invoices,
            monitor balances, mark invoices
            sent, and record payments.
          </p>
        </div>

        <div className="development-admin-heading-actions">
          <button
            type="button"
            className="development-admin-button primary"
            onClick={
              openNewInvoiceForm
            }
          >
            Add invoice
          </button>
        </div>
      </div>

      {error && (
        <div
          className="development-admin-alert error"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="development-admin-alert success"
          role="status"
        >
          {success}
        </div>
      )}

      {showInvoiceForm && (
        <InvoiceForm
          formData={invoiceForm}
          clients={clients}
          projects={
            availableProjects
          }
          selectedInvoice={
            selectedInvoice
          }
          isSaving={isSaving}
          onChange={
            handleInvoiceChange
          }
          onSubmit={
            handleInvoiceSubmit
          }
          onCancel={
            closeInvoiceForm
          }
        />
      )}

      {showItemManager &&
        selectedInvoice && (
          <InvoiceManager
            invoice={
              selectedInvoice
            }
            items={invoiceItems}
            catalogItems={
              catalogItems
            }
            itemForm={itemForm}
            paymentForm={
              paymentForm
            }
            selectedItem={
              selectedItem
            }
            showItemForm={
              showItemForm
            }
            showPaymentForm={
              showPaymentForm
            }
            isLoading={
              isLoadingItems
            }
            isSaving={isSaving}
            onClose={
              closeItemManager
            }
            onAddItem={
              openNewItemForm
            }
            onEditItem={
              openEditItemForm
            }
            onDeleteItem={
              deleteItem
            }
            onItemChange={
              handleItemChange
            }
            onItemSubmit={
              handleItemSubmit
            }
            onCancelItem={() => {
              setShowItemForm(false);
              setSelectedItem(null);

              setItemForm({
                ...EMPTY_ITEM_FORM,
              });
            }}
            onOpenPayment={
              openPaymentForm
            }
            onPaymentChange={
              handlePaymentChange
            }
            onPaymentSubmit={
              handlePaymentSubmit
            }
            onCancelPayment={() =>
              setShowPaymentForm(
                false
              )
            }
          />
        )}

      <section className="development-invoice-summary-grid">
        <SummaryCard
          label="All invoices"
          value={summary.total}
          active={
            statusFilter === "all"
          }
          onClick={() =>
            setStatusFilter("all")
          }
        />

        <SummaryCard
          label="Draft"
          value={summary.draft}
          active={
            statusFilter === "draft"
          }
          onClick={() =>
            setStatusFilter("draft")
          }
        />

        <SummaryCard
          label="Outstanding"
          value={summary.outstanding}
          active={false}
          onClick={() =>
            setStatusFilter("all")
          }
        />

        <SummaryCard
          label="Paid"
          value={summary.paid}
          active={
            statusFilter === "paid"
          }
          onClick={() =>
            setStatusFilter("paid")
          }
        />

        <SummaryCard
          label="Balance due"
          value={formatCurrency(
            summary.balanceDue
          )}
          active={false}
          onClick={() =>
            setStatusFilter("all")
          }
        />
      </section>

      <section className="development-invoices-panel">
        <div className="development-invoices-toolbar">
          <div>
            <h3>
              Invoice records
            </h3>

            <p>
              {filteredInvoices.length}{" "}
              invoice
              {filteredInvoices.length ===
              1
                ? ""
                : "s"}{" "}
              shown
            </p>
          </div>

          <div className="development-invoice-filters">
            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search invoices..."
            />

            <select
              value={clientFilter}
              onChange={(event) =>
                setClientFilter(
                  event.target.value
                )
              }
            >
              <option value="">
                All clients
              </option>

              {clients.map(
                (client) => (
                  <option
                    key={client.id}
                    value={client.id}
                  >
                    {getClientName(
                      client
                    )}
                  </option>
                )
              )}
            </select>

            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >
              <option value="all">
                All statuses
              </option>

              {STATUS_OPTIONS.map(
                (status) => (
                  <option
                    key={status}
                    value={status}
                  >
                    {formatLabel(
                      status
                    )}
                  </option>
                )
              )}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="development-admin-state">
            Loading invoices...
          </div>
        ) : filteredInvoices.length ===
          0 ? (
          <div className="development-admin-empty">
            No invoices match the
            current filters.
          </div>
        ) : (
          <div className="development-invoice-card-grid">
            {filteredInvoices.map(
              (invoice) => (
                <InvoiceCard
                  key={invoice.id}
                  invoice={invoice}
                  client={clientMap.get(
                    Number(
                      invoice.client_id
                    )
                  )}
                  project={projectMap.get(
                    Number(
                      invoice.project_id
                    )
                  )}
                  onEdit={() =>
                    openEditInvoiceForm(
                      invoice
                    )
                  }
                  onManage={() =>
                    openItemManager(
                      invoice
                    )
                  }
                  onMarkSent={() =>
                    markSent(invoice)
                  }
                  onDelete={() =>
                    deleteInvoice(
                      invoice
                    )
                  }
                />
              )
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  active,
  onClick,
}) {
  return (
    <button
      type="button"
      className={`development-invoice-summary-card${
        active ? " active" : ""
      }`}
      onClick={onClick}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

function InvoiceCard({
  invoice,
  client,
  project,
  onEdit,
  onManage,
  onMarkSent,
  onDelete,
}) {
  const paid =
    Number(invoice.paid) === 1;

  const sent =
    Number(invoice.sent) === 1;

  return (
    <article className="development-invoice-card">
      <div className="development-invoice-card-header">
        <div>
          <p>
            {invoice.invoice_number}
          </p>

          <h4>
            {getClientName(client)}
          </h4>

          <span>
            {project?.title ||
              "No project"}
          </span>
        </div>

        <span
          className={`development-invoice-status ${invoice.status}`}
        >
          {formatLabel(
            invoice.status
          )}
        </span>
      </div>

      <div className="development-invoice-financial-grid">
        <div>
          <span>Total</span>
          <strong>
            {formatCurrency(
              invoice.total_amount
            )}
          </strong>
        </div>

        <div>
          <span>Paid</span>
          <strong>
            {formatCurrency(
              invoice.amount_paid
            )}
          </strong>
        </div>

        <div>
          <span>Balance</span>
          <strong>
            {formatCurrency(
              invoice.balance_due
            )}
          </strong>
        </div>

        <div>
          <span>Due</span>
          <strong>
            {formatDate(
              invoice.due_date
            )}
          </strong>
        </div>
      </div>

      <div className="development-invoice-flags">
        <span
          className={
            sent ? "active" : ""
          }
        >
          {sent ? "✓" : "○"} Sent
        </span>

        <span
          className={
            paid ? "active" : ""
          }
        >
          {paid ? "✓" : "○"} Paid
        </span>

        <span>
          {formatLabel(
            invoice.invoice_type
          )}
        </span>
      </div>

      <div className="development-invoice-card-actions">
        <button
          type="button"
          onClick={onManage}
        >
          Items & payments
        </button>

        <button
          type="button"
          onClick={onEdit}
        >
          Edit
        </button>

        {!sent && (
          <button
            type="button"
            onClick={onMarkSent}
          >
            Mark sent
          </button>
        )}

        <button
          type="button"
          className="danger"
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </article>
  );
}

function InvoiceForm({
  formData,
  clients,
  projects,
  selectedInvoice,
  isSaving,
  onChange,
  onSubmit,
  onCancel,
}) {
  return (
    <section className="development-invoice-form-panel">
      <div className="development-invoice-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            {selectedInvoice
              ? "Edit invoice"
              : "New invoice"}
          </p>

          <h3>
            {selectedInvoice
              ? selectedInvoice.invoice_number
              : "Create invoice"}
          </h3>
        </div>

        <button
          type="button"
          className="development-invoice-close-button"
          onClick={onCancel}
        >
          ×
        </button>
      </div>

      <form
        className="development-invoice-form"
        onSubmit={onSubmit}
      >
        <div className="development-invoice-form-grid">
          <SelectField
            label="Client"
            name="client_id"
            value={formData.client_id}
            onChange={onChange}
            required
          >
            <option value="">
              Select client
            </option>

            {clients.map(
              (client) => (
                <option
                  key={client.id}
                  value={client.id}
                >
                  {getClientName(
                    client
                  )}
                </option>
              )
            )}
          </SelectField>

          <SelectField
            label="Project"
            name="project_id"
            value={formData.project_id}
            onChange={onChange}
            required
          >
            <option value="">
              Select project
            </option>

            {projects.map(
              (project) => (
                <option
                  key={project.id}
                  value={project.id}
                >
                  {project.title}
                </option>
              )
            )}
          </SelectField>

          <FormField
            label="Invoice number"
            name="invoice_number"
            value={
              formData.invoice_number
            }
            onChange={onChange}
            placeholder="Generated automatically"
          />

          <SelectField
            label="Invoice type"
            name="invoice_type"
            value={
              formData.invoice_type
            }
            onChange={onChange}
          >
            {INVOICE_TYPES.map(
              (invoiceType) => (
                <option
                  key={invoiceType}
                  value={invoiceType}
                >
                  {formatLabel(
                    invoiceType
                  )}
                </option>
              )
            )}
          </SelectField>

          <FormField
            label="Issue date"
            name="issue_date"
            type="date"
            value={formData.issue_date}
            onChange={onChange}
            required
          />

          <FormField
            label="Due date"
            name="due_date"
            type="date"
            value={formData.due_date}
            onChange={onChange}
          />

          <FormField
            label="Discount amount"
            name="discount_amount"
            type="number"
            value={
              formData.discount_amount
            }
            onChange={onChange}
            min="0"
            step="0.01"
          />

          <FormField
            label="Tax amount"
            name="tax_amount"
            type="number"
            value={
              formData.tax_amount
            }
            onChange={onChange}
            min="0"
            step="0.01"
          />

          <SelectField
            label="Status"
            name="status"
            value={formData.status}
            onChange={onChange}
          >
            {STATUS_OPTIONS.map(
              (status) => (
                <option
                  key={status}
                  value={status}
                >
                  {formatLabel(
                    status
                  )}
                </option>
              )
            )}
          </SelectField>

          <SelectField
            label="Payment method"
            name="payment_method"
            value={
              formData.payment_method
            }
            onChange={onChange}
          >
            <option value="">
              Not selected
            </option>

            {PAYMENT_METHODS.map(
              (method) => (
                <option
                  key={method}
                  value={method}
                >
                  {formatLabel(
                    method
                  )}
                </option>
              )
            )}
          </SelectField>

          <TextAreaField
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={onChange}
            rows="5"
          />
        </div>

        <label className="development-invoice-checkbox">
          <input
            type="checkbox"
            name="sent"
            checked={
              Boolean(
                formData.sent
              )
            }
            onChange={onChange}
          />

          <span>
            Invoice has been sent
          </span>
        </label>

        <div className="development-invoice-form-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="development-admin-button primary"
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : selectedInvoice
                ? "Save changes"
                : "Create invoice"}
          </button>
        </div>
      </form>
    </section>
  );
}

function InvoiceManager({
  invoice,
  items,
  catalogItems,
  itemForm,
  paymentForm,
  selectedItem,
  showItemForm,
  showPaymentForm,
  isLoading,
  isSaving,
  onClose,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onItemChange,
  onItemSubmit,
  onCancelItem,
  onOpenPayment,
  onPaymentChange,
  onPaymentSubmit,
  onCancelPayment,
}) {
  return (
    <section className="development-invoice-manager-panel">
      <div className="development-invoice-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            Invoice management
          </p>

          <h3>
            {invoice.invoice_number}
          </h3>
        </div>

        <div className="development-invoice-manager-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onAddItem}
          >
            Add item
          </button>

          {Number(invoice.paid) !== 1 && (
            <button
              type="button"
              className="development-admin-button primary"
              onClick={onOpenPayment}
            >
              Record payment
            </button>
          )}

          <button
            type="button"
            className="development-invoice-close-button"
            onClick={onClose}
          >
            ×
          </button>
        </div>
      </div>

      <div className="development-invoice-manager-summary">
        <div>
          <span>Subtotal</span>
          <strong>
            {formatCurrency(
              invoice.subtotal
            )}
          </strong>
        </div>

        <div>
          <span>Total</span>
          <strong>
            {formatCurrency(
              invoice.total_amount
            )}
          </strong>
        </div>

        <div>
          <span>Paid</span>
          <strong>
            {formatCurrency(
              invoice.amount_paid
            )}
          </strong>
        </div>

        <div>
          <span>Balance</span>
          <strong>
            {formatCurrency(
              invoice.balance_due
            )}
          </strong>
        </div>
      </div>

      {showItemForm && (
        <form
          className="development-invoice-subform"
          onSubmit={onItemSubmit}
        >
          <h4>
            {selectedItem
              ? "Edit invoice item"
              : "Add invoice item"}
          </h4>

          <div className="development-invoice-form-grid">
            <SelectField
              label="Catalog item"
              name="catalog_item_id"
              value={
                itemForm.catalog_item_id
              }
              onChange={onItemChange}
            >
              <option value="">
                Custom invoice item
              </option>

              {catalogItems.map(
                (catalogItem) => (
                  <option
                    key={
                      catalogItem.id
                    }
                    value={
                      catalogItem.id
                    }
                  >
                    {
                      catalogItem.item_name
                    }
                    {" — "}
                    {formatCurrency(
                      catalogItem.default_price
                    )}
                  </option>
                )
              )}
            </SelectField>

            <FormField
              label="Title"
              name="title"
              value={itemForm.title}
              onChange={onItemChange}
              required
            />

            <FormField
              label="Quantity"
              name="quantity"
              type="number"
              value={itemForm.quantity}
              onChange={onItemChange}
              min="0.01"
              step="0.01"
            />

            <FormField
              label="Unit price"
              name="unit_price"
              type="number"
              value={
                itemForm.unit_price
              }
              onChange={onItemChange}
              min="0"
              step="0.01"
            />

            <FormField
              label="Display order"
              name="display_order"
              type="number"
              value={
                itemForm.display_order
              }
              onChange={onItemChange}
              min="0"
              step="1"
            />

            <TextAreaField
              label="Description"
              name="description"
              value={
                itemForm.description
              }
              onChange={onItemChange}
              rows="4"
            />
          </div>

          <label className="development-invoice-checkbox">
            <input
              type="checkbox"
              name="taxable"
              checked={
                Boolean(
                  itemForm.taxable
                )
              }
              onChange={onItemChange}
            />

            <span>Taxable item</span>
          </label>

          <div className="development-invoice-form-actions">
            <button
              type="button"
              className="development-admin-button secondary"
              onClick={onCancelItem}
              disabled={isSaving}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="development-admin-button primary"
              disabled={isSaving}
            >
              {isSaving
                ? "Saving..."
                : selectedItem
                  ? "Save item"
                  : "Create item"}
            </button>
          </div>
        </form>
      )}

      {showPaymentForm && (
        <form
          className="development-invoice-subform"
          onSubmit={
            onPaymentSubmit
          }
        >
          <h4>Record payment</h4>

          <div className="development-invoice-form-grid">
            <SelectField
              label="Payment type"
              name="payment_type"
              value={
                paymentForm.payment_type
              }
              onChange={
                onPaymentChange
              }
            >
              {INVOICE_TYPES.map(
                (paymentType) => (
                  <option
                    key={paymentType}
                    value={paymentType}
                  >
                    {formatLabel(
                      paymentType
                    )}
                  </option>
                )
              )}
            </SelectField>

            <FormField
              label="Amount"
              name="amount"
              type="number"
              value={
                paymentForm.amount
              }
              onChange={
                onPaymentChange
              }
              min="0.01"
              step="0.01"
              required
            />

            <SelectField
              label="Payment method"
              name="payment_method"
              value={
                paymentForm.payment_method
              }
              onChange={
                onPaymentChange
              }
            >
              {PAYMENT_METHODS.map(
                (method) => (
                  <option
                    key={method}
                    value={method}
                  >
                    {formatLabel(
                      method
                    )}
                  </option>
                )
              )}
            </SelectField>

            <FormField
              label="Reference number"
              name="reference_number"
              value={
                paymentForm.reference_number
              }
              onChange={
                onPaymentChange
              }
            />

            <TextAreaField
              label="Notes"
              name="notes"
              value={
                paymentForm.notes
              }
              onChange={
                onPaymentChange
              }
              rows="3"
            />
          </div>

          <label className="development-invoice-checkbox">
            <input
              type="checkbox"
              name="confirmed"
              checked={
                Boolean(
                  paymentForm.confirmed
                )
              }
              onChange={
                onPaymentChange
              }
            />

            <span>
              Payment confirmed
            </span>
          </label>

          <div className="development-invoice-form-actions">
            <button
              type="button"
              className="development-admin-button secondary"
              onClick={
                onCancelPayment
              }
              disabled={isSaving}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="development-admin-button primary"
              disabled={isSaving}
            >
              {isSaving
                ? "Saving..."
                : "Record payment"}
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="development-admin-state">
          Loading invoice items...
        </div>
      ) : items.length === 0 ? (
        <div className="development-admin-empty">
          No items have been added to
          this invoice.
        </div>
      ) : (
        <div className="development-invoice-item-table-wrapper">
          <table className="development-invoice-item-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Quantity</th>
                <th>Unit price</th>
                <th>Total</th>
                <th>Taxable</th>
                <th />
              </tr>
            </thead>

            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>
                      {item.title}
                    </strong>

                    {item.catalog_item_name && (
                      <small>
                        Catalog:{" "}
                        {
                          item.catalog_item_name
                        }
                      </small>
                    )}

                    <span>
                      {item.description ||
                        "No description"}
                    </span>
                  </td>

                  <td>{item.quantity}</td>

                  <td>
                    {formatCurrency(
                      item.unit_price
                    )}
                  </td>

                  <td>
                    {formatCurrency(
                      item.line_total
                    )}
                  </td>

                  <td>
                    {Number(
                      item.taxable
                    ) === 1
                      ? "Yes"
                      : "No"}
                  </td>

                  <td>
                    <div className="development-invoice-item-actions">
                      <button
                        type="button"
                        onClick={() =>
                          onEditItem(item)
                        }
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        className="danger"
                        onClick={() =>
                          onDeleteItem(
                            item
                          )
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function FormField({
  label,
  name,
  type = "text",
  value,
  onChange,
  required = false,
  placeholder = "",
  min,
  max,
  step,
}) {
  return (
    <label className="development-invoice-field">
      <span>
        {label}
        {required && " *"}
      </span>

      <input
        type={type}
        name={name}
        value={safeString(value)}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
      />
    </label>
  );
}

function SelectField({
  label,
  name,
  value,
  onChange,
  required = false,
  children,
}) {
  return (
    <label className="development-invoice-field">
      <span>
        {label}
        {required && " *"}
      </span>

      <select
        name={name}
        value={safeString(value)}
        onChange={onChange}
        required={required}
      >
        {children}
      </select>
    </label>
  );
}

function TextAreaField({
  label,
  name,
  value,
  onChange,
  rows = "4",
}) {
  return (
    <label className="development-invoice-field full-width">
      <span>{label}</span>

      <textarea
        name={name}
        value={safeString(value)}
        onChange={onChange}
        rows={rows}
      />
    </label>
  );
}

export default DevelopmentInvoices;